import React, { memo, useCallback, useEffect, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { InternalAccessTabs } from 'src/components/internal-access/InternalAccessTabs'
import { InternalRolesTab } from 'src/components/internal-access/roles/InternalRolesTab'
import { InternalUsersTab } from 'src/components/internal-access/users/InternalUsersTab'
import { SectionCard } from 'src/components/shared/SectionCard'
import { AdminFetchInternalRoles, AdminFetchInternalUsers } from 'src/constants/actionTypes'
import { InternalAccessTab } from 'src/constants/internal-access/internalAccessTabs'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { useHasPermission } from 'src/hooks/useHasPermission'
import { resolveInternalAccessTab } from 'src/utils/internalAccess/resolveInternalAccessTab'

/**
 * Users Management — a standalone admin app (reached from the Launcher), laid out like
 * `SchedulersManagementPage`: an h2, then tabs and the table card filling the viewport.
 *
 * The tab lives in the QUERY STRING so Back from the role editor returns to the Roles tab. Both
 * lists load here, whichever tab is open: both tab subtitles show counts, and the Roles table
 * counts users per role.
 */
const UsersManagementPageComponent = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const canViewUsers = useHasPermission(InternalPermission.InternalUsersView)
  const canViewRoles = useHasPermission(InternalPermission.InternalRolesView)

  const selectedTab = useMemo(() => {
    return resolveInternalAccessTab(searchParams.get('tab'), canViewUsers, canViewRoles)
  }, [canViewRoles, canViewUsers, searchParams])

  const availableTabs = useMemo(() => {
    const tabs: InternalAccessTab[] = []

    if (canViewUsers) {
      tabs.push(InternalAccessTab.Users)
    }

    if (canViewRoles) {
      tabs.push(InternalAccessTab.Roles)
    }

    return tabs
  }, [canViewRoles, canViewUsers])

  const fetchUsers = useCallback(() => {
    if (!isAuthenticated || !canViewUsers) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchInternalUsers.request())
  }, [asyncDispatchWithErrorToast, canViewUsers, isAuthenticated])

  const fetchRoles = useCallback(() => {
    if (!isAuthenticated || !canViewRoles) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchInternalRoles.request())
  }, [asyncDispatchWithErrorToast, canViewRoles, isAuthenticated])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  useEffect(() => {
    fetchRoles()
  }, [fetchRoles])

  const handleTabChange = useCallback(
    (tab: InternalAccessTab) => {
      const nextParams = new URLSearchParams(searchParams)

      nextParams.set('tab', tab)

      setSearchParams(nextParams, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  return (
    <div
      data-testid="users-management-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-6`}
    >
      <div css={tw`mb-6 flex shrink-0 flex-row items-center justify-between gap-4`}>
        <Typography variant="h2">Users Management</Typography>
      </div>
      {selectedTab === null ? (
        <SectionCard title="No access">
          <Typography variant="body2" css={tw`text-shades-700`}>
            You don&apos;t have access to users or roles.
          </Typography>
        </SectionCard>
      ) : null}
      {selectedTab !== null ? (
        <div css={tw`shrink-0`}>
          <InternalAccessTabs
            selectedTab={selectedTab}
            availableTabs={availableTabs}
            onChange={handleTabChange}
          />
        </div>
      ) : null}
      {selectedTab === InternalAccessTab.Users ? <InternalUsersTab onRefresh={fetchUsers} /> : null}
      {selectedTab === InternalAccessTab.Roles ? <InternalRolesTab onRefresh={fetchRoles} /> : null}
    </div>
  )
}

export const UsersManagementPage = memo(UsersManagementPageComponent)
